'use client';

import React, { CSSProperties } from 'react';

import { parseToRgb, readableColor } from 'polished';

import { useTheme } from 'contexts/ThemeContext';

import styles from './styles.module.css';

interface PulseProps {
	/**
	 * The size of the indicator in pixels. This controls both the width and height of the circle.
	 * @default 20
	 */
	size?: number;

	/**
	 * The ratio of the pulse size relative to the indicator size. For example, 0.6 means the pulse will be 60% of the indicator size.
	 * @default 0.6
	 */
	pulseRatio?: number;

	/**
	 * The color of the pulse effect. If not provided, it defaults to the value of `color` with reduced opacity.
	 */
	pulseColor?: string;

	/**
	 * The color of the indicator and the pulse effect.
	 * @default "black"
	 */
	color?: string;

	/**
	 * The duration of the pulse effect in milliseconds.
	 * @default 2000
	 * */
	duration?: number;

	/**
	 * A boolean to determine if margin should be used to fill box-shadow size.
	 * @default true
	 */
	useMargin?: boolean;
}

function Pulse({
	size = 10,
	pulseRatio = 0.8,
	useMargin = true,
	...props
}: PulseProps) {
	// Hooks
	const theme = useTheme();

	const color = props.color ?? readableColor(theme.colors.background);
	const pulseColor = props.pulseColor ?? color;
	const pulseSize = size * pulseRatio;

	const { red, green, blue } = parseToRgb(pulseColor);

	return (
		<div
			className={styles.pulse}
			style={
				{
					height: `${size}px`,
					width: `${size}px`,
					backgroundColor: color,
					margin: useMargin ? 'var(--pulse-size)' : undefined,
					'--pulse-size': `${pulseSize}px`,
					'--pulse-color': [red, green, blue].join(', '),
				} as CSSProperties
			}
		/>
	);
}

export default Pulse;
